import { useState, useEffect, useRef } from 'react';

export default function UnifiedImporter() {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [activeView, setActiveView] = useState<'before' | 'after'>('before');
  const [dropdowns, setDropdowns] = useState({
    propertyType: false,
    designStyle: false,
    budget: false,
    area: false
  });
  const [selectedFilters, setSelectedFilters] = useState({
    propertyType: 'Property Type',
    designStyle: 'Design',
    budget: 'Budget',
    area: 'Area'
  });
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Floating content particles state
  const [particles, setParticles] = useState<Array<{ id: number; icon: string; color: string; delay: number; yOffset: number }>>([]);

  useEffect(() => {
    // Generate floating particles
    const icons = [
      { icon: 'ri-image-line', color: '#E4405F' },
      { icon: 'ri-video-line', color: '#00C4CC' },
      { icon: 'ri-article-line', color: '#FBBC04' },
      { icon: 'ri-palette-line', color: '#E60023' },
      { icon: 'ri-file-pdf-line', color: '#4285F4' },
      { icon: 'ri-instagram-line', color: '#833AB4' },
      { icon: 'ri-pinterest-line', color: '#E60023' },
      { icon: 'ri-slideshow-line', color: '#34A853' },
    ];
    
    const newParticles = Array.from({ length: 10 }, (_, i) => ({
      id: i,
      ...icons[i % icons.length],
      delay: i * 0.6,
      yOffset: 15 + (i * 8) % 70
    }));
    setParticles(newParticles);
  }, []);

  // Auto-toggle between Before and After views every 5 seconds
  useEffect(() => {
    const toggleInterval = setInterval(() => {
      setActiveView(prev => prev === 'before' ? 'after' : 'before');
    }, 5000);
    return () => clearInterval(toggleInterval);
  }, []);

  const filterOptions = {
    propertyType: ['All Types', 'Apartment', 'Villa', 'Penthouse', 'Office', 'Retail'],
    designStyle: ['All Styles', 'Modern', 'Contemporary', 'Traditional', 'Minimalist', 'Luxury'],
    budget: ['All Budgets', '₹10-25 Lakhs', '₹25-50 Lakhs', '₹50L-1 Cr', '₹1 Cr+'],
    area: ['All Areas', '500-1000 sqft', '1000-2000 sqft', '2000-3000 sqft', '3000+ sqft']
  };

  const sources = [
    { name: 'Videos', icon: 'ri-video-line', color: '#E4405F', format: '' },
    { name: 'Images', icon: 'ri-palette-line', color: '#00C4CC', format: 'Designs & Presentations' },
    { name: 'Home Tours', icon: 'ri-pinterest-line', color: '#E60023', format: 'Boards & Pins' },
    { name: 'Know How', icon: 'ri-google-fill', color: '#4285F4', format: 'Docs & Folders' },
    { name: 'Drawings', icon: 'ri-file-pdf-line', color: '#FF0000', format: 'Portfolios & Catalogs' }
  ];

  const formats = [
    { name: 'Instagram', icon: 'ri-video-line', color: '#E4405F' },
    { name: 'PDFs', icon: 'ri-image-line', color: '#4285F4' },
    { name: 'Canva', icon: 'ri-article-line', color: '#FF0000' },
    { name: 'Issu', icon: 'ri-lightbulb-line', color: '#FF6B00' },
    { name: 'Blogs', icon: 'ri-pencil-ruler-2-line', color: '#00C4CC' }
  ];

  useEffect(() => {
    const scrollInterval = setInterval(() => {
      if (!isHovering) {
        setScrollPosition(prev => {
          const maxScroll = 200;
          if (prev >= maxScroll) {
            return 0;
          }
          return prev + 0.5;
        });
      }
    }, 50);
    return () => clearInterval(scrollInterval);
  }, [isHovering]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdowns({ propertyType: false, designStyle: false, budget: false, area: false });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (key: keyof typeof dropdowns) => {
    setDropdowns(prev => ({
      propertyType: false,
      designStyle: false,
      budget: false,
      area: false,
      [key]: !prev[key]
    }));
  };

  const selectOption = (key: keyof typeof selectedFilters, value: string) => {
    setSelectedFilters(prev => ({ ...prev, [key]: value }));
    setDropdowns(prev => ({ ...prev, [key]: false }));
  };

  const platforms = [
    { name: 'Instagram', icon: 'ri-instagram-line', color: '#E4405F', bgGradient: 'from-[#833AB4] via-[#E4405F] to-[#FCAF45]' },
    { name: 'Canva', icon: 'ri-palette-line', color: '#00C4CC', bgGradient: 'from-[#00C4CC] to-[#7B2CBF]' },
    { name: 'Google Slides', icon: 'ri-slideshow-line', color: '#FBBC04', bgGradient: 'from-[#FBBC04] to-[#EA4335]' },
    { name: 'Pinterest', icon: 'ri-pinterest-line', color: '#E60023', bgGradient: 'from-[#E60023] to-[#BD081C]' },
    { name: 'Google Business', icon: 'ri-google-fill', color: '#4285F4', bgGradient: 'from-[#4285F4] to-[#34A853]' },
    { name: 'Website', icon: 'ri-global-line', color: '#6366F1', bgGradient: 'from-[#6366F1] to-[#8B5CF6]' }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-24 bg-[#F8F9FA] relative overflow-hidden">
      {/* Animated Background Flow Lines */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Flowing lines from left to right */}
        <svg className="absolute inset-0 w-full h-full" style={{ opacity: 0.15 }}>
          <defs>
            <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#EF4444" />
              <stop offset="50%" stopColor="#35C5F0" />
              <stop offset="100%" stopColor="#10B981" />
            </linearGradient>
          </defs>
          {[0, 1, 2, 3, 4].map((i) => (
            <path
              key={i}
              d={`M 0 ${200 + i * 80} Q 400 ${150 + i * 80} 800 ${200 + i * 80} T 1600 ${200 + i * 80}`}
              fill="none"
              stroke="url(#flowGradient)"
              strokeWidth="2"
              className="animate-flow-line"
              style={{ animationDelay: `${i * 0.3}s` }}
            />
          ))}
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-block px-3 sm:px-4 py-2 bg-[#35C5F0]/10 border border-[#35C5F0]/30 rounded-full text-[#35C5F0] text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
            The Solution
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#2F3438] mb-4 sm:mb-6">
            Everything you've built—finally in one place
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-[#ABB2B9] max-w-3xl mx-auto px-4">
            Turn scattered content into one authoritative portfolio
          </p>
        </div>

        {/* Visual Flow - Multi-Platform Sync */}
        <div className="relative">
          {/* Floating Content Particles - only show during transition */}
          <div className={`hidden lg:block absolute inset-0 pointer-events-none z-20 transition-opacity duration-500 ${activeView === 'before' ? 'opacity-0' : 'opacity-100'}`}>
            {particles.map((particle) => (
              <div
                key={particle.id}
                className="absolute animate-float-to-right"
                style={{
                  left: '20%',
                  top: `${particle.yOffset}%`,
                  animationDelay: `${particle.delay}s`,
                  animationDuration: '3.5s',
                }}
              >
                <div 
                  className="w-9 h-9 rounded-lg flex items-center justify-center shadow-lg animate-pulse-glow backdrop-blur-sm"
                  style={{ backgroundColor: `${particle.color}25`, border: `2px solid ${particle.color}50` }}
                >
                  <i className={`${particle.icon} text-base`} style={{ color: particle.color }}></i>
                </div>
              </div>
            ))}
          </div>

          {/* Centered Container for Before/After Views */}
          <div className="flex justify-center items-center min-h-[750px] lg:min-h-[800px] relative">
            {/* Before View - Connected Platforms */}
            <div 
              className={`absolute transition-all duration-700 ease-in-out ${
                activeView === 'before' 
                  ? 'opacity-100 scale-100 translate-x-0' 
                  : 'opacity-0 scale-95 -translate-x-20 pointer-events-none'
              }`}
            >
              <div className="w-[300px] sm:w-[340px] flex flex-col items-center">
                {/* Prominent Before Label */}
                <div className="mb-6 text-center">
                  <span className="text-3xl sm:text-4xl font-bold text-red-500">Before</span>
                </div>
                {/* Scattered Label */}
                <div className="flex items-center justify-center gap-2 mb-4 w-full">
                  <div className="h-px flex-1 bg-red-300"></div>
                  <span className="text-red-500 font-bold text-sm uppercase tracking-wider">Scattered</span>
                  <div className="h-px flex-1 bg-red-300"></div>
                </div>
                {/* Scattered Platforms Container with red tint */}
                <div className="space-y-3 bg-red-50/50 border border-red-200/60 rounded-2xl p-4 relative w-full">
                  {/* Outgoing particles effect */}
                  <div className="absolute -right-2 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-3">
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className="w-2 h-2 bg-[#35C5F0] rounded-full animate-particle-emit"
                        style={{ animationDelay: `${i * 0.4}s` }}
                      ></div>
                    ))}
                  </div>
                  
                  {[
                    { name: 'Instagram', icon: 'ri-instagram-line', gradient: 'from-[#833AB4] via-[#E4405F] to-[#FCAF45]' },
                    { name: 'Canva', icon: 'ri-palette-line', gradient: 'from-[#00C4CC] to-[#7B2CBF]' },
                    { name: 'Google Slides', icon: 'ri-slideshow-line', gradient: 'from-[#FBBC04] to-[#EA4335]' },
                    { name: 'Pinterest', icon: 'ri-pinterest-line', gradient: 'from-[#E60023] to-[#BD081C]' },
                    { name: 'Google Business', icon: 'ri-google-line', gradient: 'from-[#4285F4] to-[#34A853]' },
                    { name: 'Website', icon: 'ri-global-line', gradient: 'from-[#667EEA] to-[#764BA2]' }
                  ].map((platform, index) => (
                    <div key={platform.name} className="relative">
                      <div 
                        className="bg-white rounded-xl p-3 sm:p-4 shadow-md border border-red-100 flex items-center gap-3 hover:shadow-lg transition-shadow animate-subtle-shake"
                        style={{ animationDelay: `${index * 0.2}s` }}
                      >
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${platform.gradient} flex items-center justify-center flex-shrink-0`}>
                          <i className={`${platform.icon} text-white text-lg`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-[#2F3438]">{platform.name}</div>
                        </div>
                        {/* Disconnected indicator with pulse */}
                        <div className="w-2 h-2 bg-red-300 rounded-full opacity-60 animate-pulse"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* After View - WowHouse Priya Studio Profile */}
            <div 
              className={`absolute transition-all duration-700 ease-in-out ${
                activeView === 'after' 
                  ? 'opacity-100 scale-100 translate-x-0' 
                  : 'opacity-0 scale-95 translate-x-20 pointer-events-none'
              }`}
            >
              <div className="w-[300px] sm:w-[340px] flex flex-col items-center">
                {/* Prominent After Label */}
                <div className="mb-6 text-center">
                  <span className="text-3xl sm:text-4xl font-bold text-emerald-500">After</span>
                </div>
                <div className="relative w-full">
                  {/* Incoming particles effect */}
                  <div className="absolute -left-4 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-4">
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className="w-2 h-2 bg-emerald-400 rounded-full animate-particle-receive"
                        style={{ animationDelay: `${i * 0.5}s` }}
                      ></div>
                    ))}
                  </div>
                  
                  {/* Unified Label */}
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <div className="h-px flex-1 bg-emerald-300"></div>
                    <span className="text-emerald-600 font-bold text-sm uppercase tracking-wider animate-pulse">Unified</span>
                    <div className="h-px flex-1 bg-emerald-300"></div>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-400 to-[#35C5F0] p-0.5 rounded-2xl shadow-xl animate-border-glow">
                    <div className="bg-white rounded-2xl p-5 sm:p-6 w-full" ref={dropdownRef}>
                      {/* WowHouse Header */}
                      <div className="flex items-center gap-3 mb-3 pb-3 border-b border-[#E5E7EB]">
                        <div className="w-12 h-12 rounded-full bg-[#35C5F0]/20 flex items-center justify-center">
                          <i className="ri-home-smile-fill text-[#35C5F0] text-xl"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-[#2F3438] truncate">Priya Studio</div>
                          <div className="text-xs text-[#6B7280]">Interior Designer • Mumbai</div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs font-semibold text-[#2F3438]">45 projects</span>
                            <span className="text-[#ABB2B9]">•</span>
                            <div className="flex items-center gap-1">
                              <span className="text-xs font-semibold text-[#2F3438]">4.8</span>
                              <i className="ri-star-fill text-[#FBBF24] text-xs"></i>
                              <span className="text-xs text-[#6B7280]">(127)</span>
                            </div>
                          </div>
                        </div>
                        <i className="ri-verified-badge-fill text-[#35C5F0] text-2xl"></i>
                      </div>
                      
                      {/* Organized Content Sections */}
                      <div className="space-y-3">
                        {/* Home Tours */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-bold text-[#2F3438]">Home Tours</span>
                          </div>
                          {/* Filters - Now above thumbnail */}
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {/* Property Type Filter */}
                            <div className="relative">
                              <button
                                onClick={() => toggleDropdown('propertyType')}
                                className="flex items-center gap-1 px-2 py-1 bg-[#F5F5F5] rounded text-[9px] text-[#6B7280] hover:bg-[#E5E7EB] transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <span>{selectedFilters.propertyType}</span>
                                <i className={`ri-arrow-down-s-line text-[10px] transition-transform ${dropdowns.propertyType ? 'rotate-180' : ''}`}></i>
                              </button>
                              {dropdowns.propertyType && (
                                <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-[#E5E7EB] py-1 z-20 min-w-[100px]">
                                  {filterOptions.propertyType.map((option) => (
                                    <button
                                      key={option}
                                      onClick={() => selectOption('propertyType', option)}
                                      className="w-full text-left px-3 py-1.5 text-[9px] text-[#6B7280] hover:bg-[#F5F5F5] cursor-pointer whitespace-nowrap"
                                    >
                                      {option}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            {/* Design Filter */}
                            <div className="relative">
                              <button
                                onClick={() => toggleDropdown('designStyle')}
                                className="flex items-center gap-1 px-2 py-1 bg-[#F5F5F5] rounded text-[9px] text-[#6B7280] hover:bg-[#E5E7EB] transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <span>{selectedFilters.designStyle}</span>
                                <i className={`ri-arrow-down-s-line text-[10px] transition-transform ${dropdowns.designStyle ? 'rotate-180' : ''}`}></i>
                              </button>
                              {dropdowns.designStyle && (
                                <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-[#E5E7EB] py-1 z-20 min-w-[100px]">
                                  {filterOptions.designStyle.map((option) => (
                                    <button
                                      key={option}
                                      onClick={() => selectOption('designStyle', option)}
                                      className="w-full text-left px-3 py-1.5 text-[9px] text-[#6B7280] hover:bg-[#F5F5F5] cursor-pointer whitespace-nowrap"
                                    >
                                      {option}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            {/* Budget Filter */}
                            <div className="relative">
                              <button
                                onClick={() => toggleDropdown('budget')}
                                className="flex items-center gap-1 px-2 py-1 bg-[#F5F5F5] rounded text-[9px] text-[#6B7280] hover:bg-[#E5E7EB] transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <span>{selectedFilters.budget}</span>
                                <i className={`ri-arrow-down-s-line text-[10px] transition-transform ${dropdowns.budget ? 'rotate-180' : ''}`}></i>
                              </button>
                              {dropdowns.budget && (
                                <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-[#E5E7EB] py-1 z-20 min-w-[100px]">
                                  {filterOptions.budget.map((option) => (
                                    <button
                                      key={option}
                                      onClick={() => selectOption('budget', option)}
                                      className="w-full text-left px-3 py-1.5 text-[9px] text-[#6B7280] hover:bg-[#F5F5F5] cursor-pointer whitespace-nowrap"
                                    >
                                      {option}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            {/* Area Filter */}
                            <div className="relative">
                              <button
                                onClick={() => toggleDropdown('area')}
                                className="flex items-center gap-1 px-2 py-1 bg-[#F5F5F5] rounded text-[9px] text-[#6B7280] hover:bg-[#E5E7EB] transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <span>{selectedFilters.area}</span>
                                <i className={`ri-arrow-down-s-line text-[10px] transition-transform ${dropdowns.area ? 'rotate-180' : ''}`}></i>
                              </button>
                              {dropdowns.area && (
                                <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border border-[#E5E7EB] py-1 z-20 min-w-[100px]">
                                  {filterOptions.area.map((option) => (
                                    <button
                                      key={option}
                                      onClick={() => selectOption('area', option)}
                                      className="w-full text-left px-3 py-1.5 text-[9px] text-[#6B7280] hover:bg-[#F5F5F5] cursor-pointer whitespace-nowrap"
                                    >
                                      {option}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Home Tour Thumbnail - Blog Style */}
                          <div className="relative rounded-lg overflow-hidden">
                            <img
                              src="https://public.readdy.ai/ai/img_res/1708abd34109a95e7c723ded80332757.jpg"
                              alt="Home Tour"
                              className="w-full h-[120px] object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                            <div className="absolute bottom-2 left-2 right-2">
                              <div className="text-white text-xs font-semibold">Modern 3BHK Apartment Makeover</div>
                              <div className="text-white/70 text-[10px]">Mumbai • 2,400 sqft</div>
                            </div>
                            <div className="absolute top-2 right-2 bg-white/90 rounded px-1.5 py-0.5">
                              <i className="ri-article-fill text-[#35C5F0] text-xs"></i>
                            </div>
                          </div>
                        </div>

                        {/* Photos & Videos - 2 Lines (8 items) */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-bold text-[#2F3438]">Photos & Videos</span>
                          </div>
                          <div className="grid grid-cols-4 gap-1">
                            <div className="aspect-square rounded overflow-hidden bg-[#F5F5F5] relative">
                              <img
                                src="https://readdy.ai/api/search-image?query=bright%20sunlit%20living%20room%20interior%20morning%20light%20streaming%20through%20windows%20cozy%20sofa%20with%20throw%20blanket%20coffee%20cup%20on%20table%20subtle%20signs%20of%20life%20warm%20home%20atmosphere%20professional%20photography&width=100&height=100&seq=priya-sunlight-1&orientation=squarish"
                                alt="Photo 1"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="aspect-square rounded overflow-hidden bg-[#F5F5F5] relative">
                              <img
                                src="https://readdy.ai/api/search-image?query=sunlit%20bedroom%20interior%20golden%20hour%20light%20white%20linen%20bedding%20open%20book%20on%20nightstand%20fresh%20flowers%20warm%20cozy%20atmosphere%20subtle%20signs%20of%20life%20professional%20photography&width=100&height=100&seq=priya-sunlight-2&orientation=squarish"
                                alt="Photo 2"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="aspect-square rounded overflow-hidden bg-[#F5F5F5] relative">
                              <img
                                src="https://readdy.ai/api/search-image?query=bright%20kitchen%20interior%20morning%20sunlight%20streaming%20through%20window%20fresh%20fruit%20bowl%20on%20counter%20herbs%20in%20pots%20warm%20home%20atmosphere%20subtle%20signs%20of%20life%20professional%20photography&width=100&height=100&seq=priya-sunlight-3&orientation=squarish"
                                alt="Photo 3"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="aspect-square rounded overflow-hidden bg-[#F5F5F5] relative">
                              <img
                                src="https://readdy.ai/api/search-image?query=sunlit%20dining%20room%20interior%20natural%20light%20wooden%20table%20with%20fresh%20flowers%20tea%20set%20warm%20inviting%20atmosphere%20subtle%20signs%20of%20life%20professional%20photography&width=100&height=100&seq=priya-sunlight-4&orientation=squarish"
                                alt="Photo 4"
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                <i className="ri-play-fill text-white text-sm"></i>
                              </div>
                            </div>
                            <div className="aspect-square rounded overflow-hidden bg-[#F5F5F5] relative">
                              <img
                                src="https://readdy.ai/api/search-image?query=cozy%20reading%20nook%20by%20window%20afternoon%20sunlight%20comfortable%20armchair%20with%20cushion%20open%20magazine%20warm%20blanket%20subtle%20signs%20of%20life%20professional%20photography&width=100&height=100&seq=priya-sunlight-5&orientation=squarish"
                                alt="Photo 5"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="aspect-square rounded overflow-hidden bg-[#F5F5F5] relative">
                              <img
                                src="https://readdy.ai/api/search-image?query=sunlit%20bathroom%20interior%20morning%20light%20white%20tiles%20fresh%20towels%20plant%20on%20counter%20soap%20dispenser%20warm%20spa%20atmosphere%20subtle%20signs%20of%20life%20professional%20photography&width=100&height=100&seq=priya-sunlight-6&orientation=squarish"
                                alt="Photo 6"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="aspect-square rounded overflow-hidden bg-[#F5F5F5] relative">
                              <img
                                src="https://readdy.ai/api/search-image?query=home%20office%20with%20natural%20sunlight%20desk%20by%20window%20laptop%20and%20coffee%20cup%20indoor%20plants%20warm%20productive%20atmosphere%20subtle%20signs%20of%20life%20professional%20photography&width=100&height=100&seq=priya-sunlight-7&orientation=squarish"
                                alt="Photo 7"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="aspect-square rounded overflow-hidden bg-[#F5F5F5] relative">
                              <img
                                src="https://readdy.ai/api/search-image?query=sunlit%20balcony%20garden%20morning%20light%20comfortable%20seating%20area%20potted%20plants%20coffee%20table%20warm%20relaxing%20atmosphere%20subtle%20signs%20of%20life%20professional%20photography&width=100&height=100&seq=priya-sunlight-8&orientation=squarish"
                                alt="Photo 8"
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                <i className="ri-play-fill text-white text-sm"></i>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Know How & Tips */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-bold text-[#2F3438]">Know How & Tips</span>
                          </div>
                          <div className="grid grid-cols-3 gap-1">
                            {[1, 2, 3].map((i) => (
                              <div key={i} className="aspect-square rounded overflow-hidden bg-[#F5F5F5] relative">
                                <img
                                  src={`https://readdy.ai/api/search-image?query=interior%20design%20tools%20materials%20samples%20wood%20flooring%20tiles%20paint%20swatches%20fabric%20textures%20construction%20materials%20professional%20flat%20lay%20photography&width=100&height=100&seq=priya-knowhow-${i}&orientation=squarish`}
                                  alt={`Tips ${i}`}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Moodboards */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-bold text-[#2F3438]">Moodboards</span>
                          </div>
                          <div className="grid grid-cols-4 gap-1">
                            {[1, 2, 3, 4].map((i) => (
                              <div key={i} className="aspect-square rounded overflow-hidden bg-[#F5F5F5] relative">
                                <img
                                  src={`https://readdy.ai/api/search-image?query=interior%20design%20moodboard%20color%20palette%20materials%20textures%20fabric%20swatches%20aesthetic%20collage&width=100&height=100&seq=priya-mood-${i}&orientation=squarish`}
                                  alt={`Moodboard ${i}`}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute top-1 right-1 w-5 h-5 bg-white/90 rounded-full flex items-center justify-center shadow-sm">
                                  <i className="ri-save-line text-[#35C5F0] text-[10px]"></i>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      {/* Bottom Nav Bar */}
                      <div className="flex items-center gap-2 border-t border-[#E5E7EB] pt-3 mt-3">
                        <button className="flex-1 bg-[#35C5F0] text-white text-xs font-semibold py-2.5 rounded-lg hover:bg-[#2BB5E0] transition-colors cursor-pointer whitespace-nowrap">
                          Request Consultation
                        </button>
                        <button className="flex items-center justify-center gap-1 px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-xs font-medium text-[#6B7280] hover:bg-[#F5F5F5] transition-colors cursor-pointer whitespace-nowrap">
                          <i className="ri-edit-line text-sm"></i>
                          Write Review
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Unified Badge - moved to bottom right */}
                  <div className="absolute -bottom-3 -right-3 bg-emerald-500 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg animate-bounce-subtle">
                    <i className="ri-check-double-line"></i>
                    All in One
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3">
              <button 
                onClick={() => setActiveView('before')}
                className={`w-3 h-3 rounded-full transition-all cursor-pointer ${activeView === 'before' ? 'bg-red-500 scale-110' : 'bg-gray-300 hover:bg-gray-400'}`}
              ></button>
              <button 
                onClick={() => setActiveView('after')}
                className={`w-3 h-3 rounded-full transition-all cursor-pointer ${activeView === 'after' ? 'bg-emerald-500 scale-110' : 'bg-gray-300 hover:bg-gray-400'}`}
              ></button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animation Styles */}
      <style>{`
        @keyframes float-to-right {
          0% {
            transform: translateX(0) translateY(0) scale(1);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateX(200px) translateY(-20px) scale(0.8);
            opacity: 0;
          }
        }
        
        @keyframes particle-emit {
          0%, 100% {
            transform: translateX(0) scale(1);
            opacity: 0.3;
          }
          50% {
            transform: translateX(20px) scale(1.5);
            opacity: 1;
          }
        }
        
        @keyframes particle-receive {
          0%, 100% {
            transform: translateX(0) scale(1.5);
            opacity: 1;
          }
          50% {
            transform: translateX(-10px) scale(1);
            opacity: 0.3;
          }
        }
        
        @keyframes flow-dash {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        @keyframes stream-flow {
          0% {
            transform: translateX(-50%) translateY(0);
            opacity: 0;
          }
          20% {
            opacity: 1;
          }
          80% {
            opacity: 1;
          }
          100% {
            transform: translateX(-50%) translateY(100px);
            opacity: 0;
          }
        }
        
        @keyframes subtle-shake {
          0%, 100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-2px);
          }
          75% {
            transform: translateX(2px);
          }
        }
        
        @keyframes glow-pulse {
          0%, 100% {
            box-shadow: 0 0 5px rgba(53, 197, 240, 0.3);
          }
          50% {
            box-shadow: 0 0 20px rgba(53, 197, 240, 0.6);
          }
        }
        
        @keyframes border-glow {
          0%, 100% {
            box-shadow: 0 0 10px rgba(16, 185, 129, 0.3), 0 0 20px rgba(53, 197, 240, 0.2);
          }
          50% {
            box-shadow: 0 0 20px rgba(16, 185, 129, 0.5), 0 0 40px rgba(53, 197, 240, 0.4);
          }
        }
        
        @keyframes bounce-subtle {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }
        
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }
          50% {
            box-shadow: 0 4px 16px rgba(53, 197, 240, 0.3);
          }
        }
        
        @keyframes flow-line {
          0% {
            stroke-dasharray: 20 80;
            stroke-dashoffset: 100;
          }
          100% {
            stroke-dasharray: 20 80;
            stroke-dashoffset: -100;
          }
        }
        
        .animate-float-to-right {
          animation: float-to-right 4s ease-in-out infinite;
        }
        
        .animate-particle-emit {
          animation: particle-emit 1.5s ease-in-out infinite;
        }
        
        .animate-particle-receive {
          animation: particle-receive 1.5s ease-in-out infinite;
        }
        
        .animate-flow-dash {
          animation: flow-dash 2s linear infinite;
        }
        
        .animate-stream-flow {
          animation: stream-flow 2s ease-in-out infinite;
        }
        
        .animate-subtle-shake {
          animation: subtle-shake 3s ease-in-out infinite;
        }
        
        .animate-glow-pulse {
          animation: glow-pulse 2s ease-in-out infinite;
        }
        
        .animate-border-glow {
          animation: border-glow 3s ease-in-out infinite;
        }
        
        .animate-bounce-subtle {
          animation: bounce-subtle 2s ease-in-out infinite;
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        
        .animate-flow-line {
          animation: flow-line 3s linear infinite;
        }
      `}</style>
    </section>
  );
}
